'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import posthog from 'posthog-js';
import { tw } from '@tw';
import { PRICE_MIN } from '@peach/types/payments/price-limits.js';
import {
  CLAIM_CATALOGUE_LIMITS,
  introFullPrice,
  introMaxPrice,
} from '@peach/types/tutors/claim-page.consts.js';
import { BIO_MAX, NAME_MAX } from '@peach/types/tutors/identity-limits.js';
import type { InviterDto } from '@peach/types/tutors/create-page.dto.js';
import { createPage } from '@/app/actions/profile';
import { InlineAuth } from '@/components/auth/InlineAuth';
import { HoursEditor } from '@/components/availability/HoursEditor';
import { validateBlocks, weekLines, type BlockSet } from '@/lib/availability/blocks';
import { callAction } from '@/lib/call-action';
import { useTimezone } from '@/lib/hooks/useTimezone';
import {
  addressProblem,
  claimedUsername,
  useAddressSuggestion,
  useAddressTyping,
} from '@/lib/page-address';
import {
  INVITE_STEPS,
  openBlocks,
  useInvitePage,
  type InvitePrices,
} from '@/lib/stores/invite-page.store';
import { InviteLayout } from './InviteLayout';
import {
  adoptInviterPrices,
  inviterFirst,
  inviterPriceLines,
  inviterWeekSet,
  samePrices,
} from './inviter-reference';
import { PagePreview } from './PagePreview';

const FIELD =
  'w-full rounded-[var(--sf-radius)] border border-[var(--sf-line)] bg-[var(--sf-card)] px-3.5 py-3 text-[16px] text-[var(--sf-ink)] placeholder:text-[var(--sf-mut)] sm:text-p-3';
const LABEL = 'text-badge font-bold text-[var(--sf-ground-fg)]';
const NOTE = 'text-p-3 leading-relaxed text-[var(--sf-ground-faint)]';

const usd = (n: number) => `$${n.toLocaleString('en-US')}`;

/** The name, settled for a beat, so the link is not looked up on every keystroke. */
function useSettled(value: string, ms = 400) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return settled;
}

function Progress({ step }: { step: number }) {
  return (
    <div
      role="img"
      className="flex gap-1.5"
      aria-label={`Step ${step + 1} of ${INVITE_STEPS.length}`}
    >
      {INVITE_STEPS.map((s, i) => (
        <span
          key={s}
          className={tw(
            'h-1 w-6 rounded-full',
            i <= step ? 'bg-[var(--sf-ground-fg)]' : 'bg-[var(--sf-ground-line)]'
          )}
        />
      ))}
    </div>
  );
}

function Question({
  step,
  title,
  sub,
  lead,
  children,
}: {
  step: number;
  title: string;
  /** A line set under the title at its weight, for what must not wrap inside the headline. */
  sub?: ReactNode;
  lead?: string;
  children: ReactNode;
}) {
  const go = useInvitePage((s) => s.go);
  return (
    <div className="flex flex-col gap-5">
      {/* One height on every step, Back or not, so the headline never jumps on Next. */}
      <div className="flex h-6 items-center justify-between gap-3">
        <Progress step={step} />
        <button
          type="button"
          onClick={() => go(step - 1)}
          aria-hidden={step === 0}
          tabIndex={step === 0 ? -1 : undefined}
          className={tw(
            'text-p-3 font-semibold text-[var(--sf-ground-faint)] underline underline-offset-4',
            step === 0 && 'invisible'
          )}
        >
          Back
        </button>
      </div>
      <div className="flex flex-col gap-1">
        <h1 className="sf-masthead">{title}</h1>
        {sub}
      </div>
      {lead && <p className={NOTE}>{lead}</p>}
      {children}
    </div>
  );
}

/**
 * The inviter's own setting beside a question, adopted with one tap and undone with another.
 * Settings only: a name or a line about yourself is theirs as a placeholder, never a value.
 */
function Reference({
  label,
  lines,
  sub,
  action,
  notes = [],
}: {
  label: string;
  lines: string[];
  sub?: string;
  action: { label: string; onClick: () => void } | 'same';
  notes?: string[];
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-3 rounded-[var(--sf-radius)] border border-[var(--sf-line)] px-3.5 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className={LABEL}>{label}</span>
          {lines.map((line) => (
            <span key={line} className="sf-num text-p-3 text-[var(--sf-ground-fg)]">
              {line}
            </span>
          ))}
          {sub && (
            <span aria-live="polite" className="text-p-3 text-[var(--sf-ground-faint)]">
              {sub}
            </span>
          )}
        </div>
        {action === 'same' ? (
          <span className="shrink-0 text-p-3 text-[var(--sf-ground-faint)]">Same as yours now</span>
        ) : (
          // `.sf-cta` is full width by its own rule, so the wrapper decides: the row's width on a
          // phone, the label's beside the text from `sm`.
          <div className="w-full shrink-0 sm:w-auto">
            <button
              type="button"
              onClick={action.onClick}
              className="sf-cta sf-cta-line mm-btn min-h-11 whitespace-nowrap px-4"
            >
              {action.label}
            </button>
          </div>
        )}
      </div>
      {notes.map((note) => (
        <p key={note} className={NOTE}>
          {note}
        </p>
      ))}
    </div>
  );
}

/** Moves on, and tells PostHog which question was answered, so drop-off is read per step. */
function useNext() {
  const go = useInvitePage((s) => s.go);
  return (step: number, props: Record<string, unknown> = {}) => {
    posthog.capture('invite_step_completed', { step: INVITE_STEPS[step], ...props });
    go(step + 1);
  };
}

function NameStep() {
  const { name, setName, address, addressTyped, setAddress, inviter } = useInvitePage();
  const next = useNext();
  const settled = useSettled(name.trim());
  // The link follows the name until it is typed into; a frozen handle never looks again.
  const handle = addressTyped ? '' : settled;
  useAddressSuggestion(handle, handle, (found) => {
    if (!useInvitePage.getState().addressTyped) setAddress(found);
  });
  const type = useAddressTyping(undefined, (state) => setAddress(state, true));
  const problem = addressProblem(address);
  const ready = !!name.trim() && address?.status === 'free';

  return (
    <Question
      step={0}
      title="What should clients call you"
      lead={inviter ? `The grey text is ${inviterFirst(inviter)}'s, as an example.` : undefined}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) next(0, { link_typed: addressTyped });
        }}
      >
        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>Your name</span>
          <input
            id="invite-name"
            autoFocus
            autoComplete="name"
            maxLength={NAME_MAX}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={inviter?.name?.trim() || 'Ivana Miljak'}
            className={FIELD}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>Your link</span>
          <span className={tw(FIELD, 'flex items-center focus-within:border-[var(--sf-ink)]')}>
            <span className="sf-num text-[var(--sf-mut)]">meetmy.io/</span>
            <input
              id="invite-link"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              value={address?.username ?? ''}
              placeholder={inviter?.username ?? 'ivana'}
              onChange={(e) => type(e.target.value)}
              aria-invalid={!!problem && address?.status !== 'checking'}
              className="sf-num min-w-0 flex-1 bg-transparent outline-none placeholder:text-[var(--sf-mut)]"
            />
            {address?.status === 'free' && (
              <span className="shrink-0 text-badge font-bold text-[var(--sf-mut)]">Free</span>
            )}
          </span>
          {problem && <span className={NOTE}>{problem}</span>}
        </label>
        <button
          type="submit"
          disabled={!ready}
          className="sf-cta sf-cta-ink mm-btn disabled:bg-[var(--sf-ticket)] disabled:text-[var(--sf-mut)]"
        >
          Next
        </button>
      </form>
    </Question>
  );
}

function WhatStep() {
  const { tagline, setTagline, inviter } = useInvitePage();
  const next = useNext();
  const ready = tagline.trim().length >= 3;
  return (
    <Question
      step={1}
      title="What do you teach and who for"
      lead={`One line, the way you would say it to a new client. It goes under your name, and your first quiz is written from it.${inviter?.tagline?.trim() ? ` The grey line is ${inviterFirst(inviter)}'s.` : ''}`}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) next(1);
        }}
      >
        <input
          id="invite-tagline"
          autoFocus
          maxLength={BIO_MAX}
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
          placeholder={inviter?.tagline?.trim() || 'Russian for adults who moved abroad'}
          aria-label="What you teach and who for"
          className={FIELD}
        />
        <button
          type="submit"
          disabled={!ready}
          className="sf-cta sf-cta-ink mm-btn disabled:bg-[var(--sf-ticket)] disabled:text-[var(--sf-mut)]"
        >
          Next
        </button>
      </form>
    </Question>
  );
}

function WeekStep({ timezone }: { timezone: string | null }) {
  const { week, setWeek, inviter } = useInvitePage();
  const next = useNext();
  const [before, setBefore] = useState<BlockSet | null>(null);
  const theirs = inviter?.week.length ? inviterWeekSet(inviter) : null;
  const same = !!theirs && weekLines(theirs).join() === weekLines(week).join();
  const zone = (z: string | null) => (z ? z.replace(/_/g, ' ') : '');
  const open = openBlocks(week);
  const errors = validateBlocks(week.blocks.filter((b) => !week.disabledDays.has(b.day)));
  const ready = open.length > 0 && errors.size === 0;
  return (
    <Question
      step={2}
      title="When can clients book you"
      lead={
        timezone
          ? `Times in ${timezone.replace(/_/g, ' ')}. You can change them any time.`
          : undefined
      }
    >
      {inviter && theirs && (
        <Reference
          label={`${inviterFirst(inviter)}'s week`}
          lines={weekLines(theirs)}
          sub={
            before
              ? inviter.timezone === timezone
                ? 'Set'
                : `Same clock times, in ${zone(timezone)}`
              : inviter.timezone
                ? `in ${zone(inviter.timezone)}`
                : undefined
          }
          action={
            before
              ? {
                  label: 'Undo',
                  onClick: () => {
                    setWeek(before);
                    setBefore(null);
                  },
                }
              : same
                ? 'same'
                : {
                    label: 'Use these times',
                    onClick: () => {
                      setBefore(week);
                      setWeek(theirs);
                    },
                  }
          }
        />
      )}
      <div className="mb-2 rounded-[var(--sf-radius)] bg-[var(--sf-card)] px-3.5 text-[var(--sf-ink)]">
        <HoursEditor week={week} onWeek={setWeek} idPrefix="invite" />
      </div>
      {open.length === 0 && <p className={NOTE}>Open at least one day so clients can book.</p>}
      {/* The hours run past the fold; Next rides on a band of page ground, never over a row. */}
      <div className="sticky bottom-0 -mx-4 bg-[var(--sf-ground)] px-4 pb-4 pt-3 deck:mx-0 deck:px-0">
        <button
          type="button"
          disabled={!ready}
          onClick={() =>
            next(2, { days: new Set(open.map((b) => b.day)).size, used_inviter: !!before })
          }
          className="sf-cta sf-cta-ink mm-btn disabled:bg-[var(--sf-ticket)] disabled:text-[var(--sf-mut)]"
        >
          Next
        </button>
      </div>
    </Question>
  );
}

/** A whole-dollar field held inside its range, with the reason when it is not. */
function DollarField({
  id,
  label,
  value,
  min,
  max,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
}) {
  const out = !Number.isInteger(value) || value < min || value > max;
  return (
    <label className="flex flex-col gap-1.5">
      <span className={LABEL}>{label}</span>
      <span className={tw(FIELD, 'flex items-center gap-1')}>
        <span className="text-[var(--sf-mut)]">$</span>
        <input
          id={id}
          inputMode="numeric"
          value={Number.isNaN(value) ? '' : String(value)}
          onChange={(e) => onChange(Number(e.target.value.replace(/\D/g, '') || NaN))}
          aria-invalid={out}
          className="sf-num min-w-0 flex-1 bg-transparent outline-none"
        />
      </span>
      {out && (
        <span className={NOTE}>
          Between {usd(min)} and {usd(max)}.
        </span>
      )}
    </label>
  );
}

/** What the intro is measured against, said where its price is typed. */
function IntroDiscount({ prices }: { prices: InvitePrices }) {
  const full = introFullPrice(prices.hourPrice, prices.introMinutes);
  if (full <= prices.introPrice) {
    return (
      <p className={NOTE}>
        At this hour a {prices.introMinutes}-minute intro has no room for a discount. A longer intro
        or a higher hour gives it one.
      </p>
    );
  }
  const off = Math.round((1 - prices.introPrice / full) * 100);
  return (
    <p className={NOTE}>
      Clients see {usd(full)} crossed out, {off}% off: {prices.introMinutes} minutes of your hour.
    </p>
  );
}

function PricesStep() {
  const { prices, priced, setPrices, inviter } = useInvitePage();
  const next = useNext();
  const [draft, setDraft] = useState<InvitePrices>(prices);
  const [before, setBefore] = useState<InvitePrices | null>(null);
  const theirLines = inviter ? inviterPriceLines(inviter) : [];
  const adopted = inviter ? adoptInviterPrices(inviter, draft) : null;
  const { hourPrice, introPrice, introMinutes } = CLAIM_CATALOGUE_LIMITS;
  // The intro is always a discount on the hour (`introMaxPrice`), so its cap moves with the hour.
  const introCap = (p: InvitePrices) =>
    Number.isInteger(p.hourPrice)
      ? Math.max(PRICE_MIN, introMaxPrice(p.hourPrice, p.introMinutes))
      : introPrice.max;
  const validPrices = (p: InvitePrices) =>
    Number.isInteger(p.introPrice) &&
    p.introPrice >= PRICE_MIN &&
    p.introPrice <= introCap(p) &&
    Number.isInteger(p.hourPrice) &&
    p.hourPrice >= hourPrice.min &&
    p.hourPrice <= hourPrice.max;
  const valid = validPrices(draft);
  // Every valid edit lands on the page beside the question at once; a half-typed price waits.
  const edit = (next: InvitePrices) => {
    setDraft(next);
    if (validPrices(next)) setPrices(next);
  };
  const [lowered, setLowered] = useState<number | null>(null);
  // A shorter intro or a cheaper hour moves the cap under a price that was fine: the intro comes
  // down to fit and says so, rather than leaving Next locked behind a range error.
  const fitIntro = (next: InvitePrices) => {
    const cap = introCap(next);
    const fits = !Number.isInteger(next.hourPrice) || next.introPrice <= cap;
    setLowered(fits ? null : cap);
    edit(fits ? next : { ...next, introPrice: cap });
  };

  return (
    <Question
      step={3}
      title="What do you charge"
      lead="Clients see these on your page. Packs are worked out from your hour, 5% off each call, and you can change any of it later."
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid) return;
          next(3, { changed: priced, used_inviter: !!before });
        }}
      >
        {inviter && adopted && theirLines.length > 0 && (
          <Reference
            label={`${inviterFirst(inviter)}'s prices`}
            lines={theirLines}
            notes={before ? adopted.notes : []}
            action={
              before
                ? {
                    label: 'Undo',
                    onClick: () => {
                      edit(before);
                      setBefore(null);
                    },
                  }
                : samePrices(adopted.prices, draft)
                  ? 'same'
                  : {
                      label: 'Use these prices',
                      onClick: () => {
                        setBefore(draft);
                        edit(adopted.prices);
                      },
                    }
            }
          />
        )}
        <div className="flex flex-col gap-1.5">
          <span className={LABEL}>Intro call length</span>
          <div className="flex w-fit overflow-hidden rounded-full border border-[var(--sf-line)] bg-[var(--sf-card)]">
            {introMinutes.map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={draft.introMinutes === m}
                onClick={() => fitIntro({ ...draft, introMinutes: m })}
                className={tw(
                  'px-4 py-2 text-p-3 font-semibold',
                  draft.introMinutes === m
                    ? 'bg-[var(--sf-ink)] text-[var(--sf-page)]'
                    : 'text-[var(--sf-ink)]'
                )}
              >
                {m} min
              </button>
            ))}
          </div>
        </div>
        <DollarField
          id="invite-intro-price"
          label="Intro call price"
          value={draft.introPrice}
          min={PRICE_MIN}
          max={introCap(draft)}
          onChange={(n) => edit({ ...draft, introPrice: n })}
        />
        {lowered !== null && draft.introPrice === lowered && (
          <p className={NOTE}>
            Your intro is now {usd(lowered)}, so it stays a discount on {draft.introMinutes} minutes
            of your hour.
          </p>
        )}
        {valid && <IntroDiscount prices={draft} />}
        <DollarField
          id="invite-hour-price"
          label="An hour with you"
          value={draft.hourPrice}
          min={hourPrice.min}
          max={hourPrice.max}
          onChange={(n) =>
            Number.isInteger(n) && n >= hourPrice.min
              ? fitIntro({ ...draft, hourPrice: n })
              : edit({ ...draft, hourPrice: n })
          }
        />
        <button
          type="submit"
          disabled={!valid}
          className="sf-cta sf-cta-ink mm-btn disabled:bg-[var(--sf-ticket)] disabled:text-[var(--sf-mut)]"
        >
          Next
        </button>
      </form>
    </Question>
  );
}

function PublishStep({ signedIn, timezone }: { signedIn: boolean; timezone: string | null }) {
  const { name, tagline, address, week, prices, priced, inviteCode } = useInvitePage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const username = address ? claimedUsername(address) : '';
  const once = useRef(false);

  const publish = async () => {
    if (!timezone || once.current) return;
    once.current = true;
    setBusy(true);
    setError(null);
    posthog.capture('invite_publish_pressed', {
      priced,
      signed_in_before: signedIn,
      invited: !!inviteCode,
    });
    const result = await callAction(() =>
      createPage({
        name: name.trim(),
        tagline: tagline.trim(),
        username,
        timezone,
        week: openBlocks(week),
        ...(priced ? prices : {}),
        ...(inviteCode ? { inviteCode } : {}),
      })
    );
    // A made page redirects inside the action, so only a refusal comes back here.
    once.current = false;
    setBusy(false);
    setError(result.error);
  };

  return (
    <Question
      step={4}
      title="Publish your page"
      sub={
        <p className="sf-num mt-2 hidden break-all text-head-2 font-extrabold text-[var(--sf-ground-fg)] deck:block">
          meetmy.io/{username}
        </p>
      }
      lead={
        signedIn
          ? 'Your page goes live the moment you publish. Clients can book and pay for your intro call at once.'
          : 'Your email is where bookings and payments arrive. New here? It makes your free account, and your page goes live straight after.'
      }
    >
      {signedIn ? (
        <button
          type="button"
          disabled={busy || !timezone}
          onClick={() => void publish()}
          className="sf-cta sf-cta-ink mm-btn disabled:bg-[var(--sf-ticket)] disabled:text-[var(--sf-mut)]"
        >
          {busy ? 'Publishing' : 'Publish my page'}
        </button>
      ) : (
        <InlineAuth
          id="invite-publish"
          source="invite_page"
          cta="Publish my page"
          tone="card"
          onSuccess={publish}
        />
      )}
      {error && (
        <p role="alert" className="text-p-3 font-semibold text-error">
          {error}
        </p>
      )}
    </Question>
  );
}

/**
 * The web's way to make a page, for anyone without Claude or ChatGPT: five questions,
 * the page building beside them, and the sign-in only at Publish. What it sends is
 * made by the same writer as a chat's page (`PageClaimService.makePage`).
 */
export function InviteFlow({
  signedIn,
  accountName,
  invite,
}: {
  signedIn: boolean;
  accountName: string | null;
  /** The creator whose `/invite/{code}` this is; null on a bare `/invite` or a dead code. */
  invite: { code: string; inviter: InviterDto } | null;
}) {
  const step = useInvitePage((s) => s.step);
  const timezone = useTimezone();

  useEffect(() => {
    useInvitePage.getState().setInvite(invite);
    // The code names the invite; its inviter arrives with it from the same server render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invite?.code]);

  // A signed-in account already has a name; offer it rather than ask for it twice.
  useEffect(() => {
    const s = useInvitePage.getState();
    if (accountName && !s.name) s.setName(accountName);
  }, [accountName]);

  useEffect(() => {
    posthog.capture('invite_step_viewed', { step: INVITE_STEPS[step] });
  }, [step]);

  return (
    <InviteLayout
      main={
        <>
          {invite && (
            <p className="mb-2 text-[17px] font-semibold text-[var(--sf-ground-fg)]">
              {invite.inviter.name?.trim() || invite.inviter.username} invited you to Meetmy
            </p>
          )}
          {step === 0 && <NameStep />}
          {step === 1 && <WhatStep />}
          {step === 2 && <WeekStep timezone={timezone} />}
          {step === 3 && <PricesStep />}
          {step === 4 && <PublishStep signedIn={signedIn} timezone={timezone} />}
        </>
      }
      top={
        <div className="deck:hidden">{step === 4 ? <PagePreview /> : <PagePreview compact />}</div>
      }
      aside={<PagePreview />}
      asideOnPhone={false}
    />
  );
}
